import { ref, reactive } from 'vue'

/** `notification` = the quiet in-app notification card (NOTIF-003): no coloured edge, no icon. */
export type ToastVariant = 'default' | 'notification'

export interface ToastOptions {
  title?: string
  message?: string
  duration?: number
  /** Milliseconds to show toast; alias of `duration` (if both set, `duration` wins). */
  timeout?: number
  persistent?: boolean
  showCloseButton?: boolean
  showProgress?: boolean
  showIcon?: boolean
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center'
  ariaLabel?: string
  slotContent?: any
  slotIcon?: any
  variant?: ToastVariant
}

export interface ToastItem {
  id: string
  type: 'success' | 'error' | 'warning' | 'info' | 'custom'
  variant?: ToastVariant
  title?: string
  message?: string
  duration?: number
  persistent?: boolean
  showCloseButton?: boolean
  showProgress?: boolean
  showIcon?: boolean
  ariaLabel?: string
  slotContent?: any
  slotIcon?: any
}

class ToastService {
  private toasts = ref<ToastItem[]>([])
  private containerRef: any = null

  // Register container reference
  registerContainer(container: any) {
    this.containerRef = container
  }

  // Generate unique ID
  private generateId(): string {
    return `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
  }

  // Add toast
  private addToast(type: ToastItem['type'], options: ToastOptions = {}): string {
    const { timeout, duration, ...rest } = options
    const resolvedDuration = duration ?? timeout
    const toast = {
      type,
      ...rest,
      ...(resolvedDuration !== undefined ? { duration: resolvedDuration } : {}),
    }

    if (this.containerRef) {
      return this.containerRef.addToast(toast)
    } else {
      console.warn('ToastService: No container registered')
      return ''
    }
  }

  // Public methods
  success(message: string, options: ToastOptions = {}) {
    return this.addToast('success', {
      message,
      ...options
    })
  }

  error(message: string, options: ToastOptions = {}) {
    return this.addToast('error', {
      message,
      ...options
    })
  }

  warning(message: string, options: ToastOptions = {}) {
    return this.addToast('warning', {
      message,
      ...options
    })
  }

  info(message: string, options: ToastOptions = {}) {
    return this.addToast('info', {
      message,
      ...options
    })
  }

  custom(content: any, options: ToastOptions = {}) {
    return this.addToast('custom', {
      slotContent: content,
      ...options
    })
  }

  // Dismiss specific toast
  dismiss(id: string) {
    if (this.containerRef) {
      this.containerRef.removeToast(id)
    }
  }

  // Dismiss all toasts
  dismissAll() {
    if (this.containerRef) {
      this.containerRef.clearAllToasts()
    }
  }

  // Dismiss every toast of one variant, leaving the others (NOTIF-003: the notification
  // cards go when the bell panel opens; the system toasts stay)
  dismissVariant(variant: ToastVariant) {
    if (this.containerRef) {
      this.containerRef.removeToastsWhere((item: ToastItem) => item.variant === variant)
    }
  }

  // Get all toasts
  getToasts() {
    return this.toasts.value
  }
}

// Create singleton instance
export const toast = new ToastService()

// Export the class for custom instances
export { ToastService }
